import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, type Control } from 'react-hook-form';
import { z } from 'zod';
import { Cpu, HardDrive, Mail, Plug, Sparkles } from 'lucide-react';
import {
  apiClient,
  type AiModelSelection,
  type AiProviderId,
  type AiPurpose,
  type UpdatePluggyIntegrationInput,
  type UpdateOpenAiIntegrationInput,
  type UpdateAiModelsIntegrationInput,
  type UpdateEmailSmtpIntegrationInput,
  type UpdateStorageS3IntegrationInput,
} from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-store';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return 'Erro inesperado';
}

// ---------------------------------------------------------------------------
// Pagina
// ---------------------------------------------------------------------------

export default function IntegracoesPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'pluggy-integration'],
    queryFn: () => apiClient.adminGetPluggyIntegration(),
  });

  // Campos do form
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [enabled, setEnabled] = useState(false);

  // Inicializa ao carregar
  useEffect(() => {
    if (data) {
      setEnabled(data.enabled);
      // clientId: prefill vazio — nao podemos mostrar o valor real
      // clientSecret: sempre vazio (nao e retornado pela API)
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: (input: UpdatePluggyIntegrationInput) =>
      apiClient.adminUpdatePluggyIntegration(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'pluggy-integration'] });
      // Limpa o secret apos salvar com sucesso
      setClientSecret('');
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const input: UpdatePluggyIntegrationInput = {
      clientId,
      enabled,
      // So envia secret se o usuario digitou algo
      ...(clientSecret.trim() ? { clientSecret } : {}),
    };
    mutation.mutate(input);
  }

  const isConfigured = data?.configured ?? false;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-neutral-100">Integrações</h1>
        <p className="mt-1 text-sm text-neutral-400">
          Credenciais de integrações globais da plataforma VoxisAdv.
        </p>
      </div>

      <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-6">
        <div className="mb-5 flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-neutral-800 text-neutral-300">
            <Plug className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-neutral-100">
              Pluggy — Open Finance
            </h2>
            <p className="mt-0.5 text-sm text-neutral-400">
              Credenciais da conta Pluggy do VoxisAdv para sincronização bancária dos escritórios.
            </p>
          </div>
        </div>

        {isLoading ? (
          <p className="text-sm text-neutral-500">Carregando...</p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Status atual */}
            {isConfigured && (
              <div className="rounded-md border border-neutral-700 bg-neutral-800 px-4 py-3 text-sm text-neutral-300">
                <span className="font-medium text-neutral-100">Configurado.</span>{' '}
                Client ID atual:{' '}
                <code className="rounded bg-neutral-700 px-1.5 py-0.5 font-mono text-xs text-neutral-200">
                  {data?.clientIdMasked}
                </code>
                <span className="ml-3 text-neutral-400">· Secret configurado</span>
              </div>
            )}

            {/* Client ID */}
            <div className="space-y-1.5">
              <label htmlFor="pluggy-client-id" className="block text-sm font-medium text-neutral-300">
                {isConfigured ? 'Novo Client ID' : 'Client ID'}
              </label>
              <input
                id="pluggy-client-id"
                type="text"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder={isConfigured ? 'Deixe vazio para manter o atual' : 'ex: 6718...d691'}
                className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
                required={!isConfigured}
              />
            </div>

            {/* Client Secret */}
            <div className="space-y-1.5">
              <label htmlFor="pluggy-client-secret" className="block text-sm font-medium text-neutral-300">
                {isConfigured ? 'Novo Client Secret' : 'Client Secret'}
              </label>
              <input
                id="pluggy-client-secret"
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder={isConfigured ? 'Deixe vazio para manter o atual' : 'Cole o secret aqui'}
                className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
                required={!isConfigured}
                autoComplete="new-password"
              />
              {isConfigured && (
                <p className="text-xs text-neutral-500">
                  O secret atual é mantido se este campo ficar vazio.
                </p>
              )}
            </div>

            {/* Toggle ativo */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                onClick={() => setEnabled((v) => !v)}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2 focus:ring-offset-neutral-900 ${
                  enabled ? 'bg-violet-600' : 'bg-neutral-700'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    enabled ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className="text-sm text-neutral-300">
                Integração {enabled ? 'ativa' : 'desativada'}
              </span>
            </div>

            {/* Erro */}
            {mutation.isError && (
              <p className="text-sm text-red-400">{getErrorMessage(mutation.error)}</p>
            )}

            {/* Sucesso */}
            {mutation.isSuccess && (
              <p className="text-sm text-green-400">Configuração salva com sucesso.</p>
            )}

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={mutation.isPending}
                className="rounded-md bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-900 hover:bg-white disabled:opacity-50"
              >
                {mutation.isPending ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        )}
      </div>

      <OpenAiCard />
      <AiModelsCard />
      <EmailSmtpCard />
      <StorageS3Card />
    </div>
  );
}

// ---------------------------------------------------------------------------
// OpenAI — provider do assistente de IA conversacional (ADR-0015)
// ---------------------------------------------------------------------------

function OpenAiCard() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'openai-integration'],
    queryFn: () => apiClient.adminGetOpenAiIntegration(),
  });

  const [apiKey, setApiKey] = useState('');
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (data) {
      setEnabled(data.enabled);
      // apiKey: sempre vazio (nao e retornada pela API)
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: (input: UpdateOpenAiIntegrationInput) =>
      apiClient.adminUpdateOpenAiIntegration(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'openai-integration'] });
      setApiKey('');
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const input: UpdateOpenAiIntegrationInput = {
      enabled,
      ...(apiKey.trim() ? { apiKey } : {}),
    };
    mutation.mutate(input);
  }

  const isConfigured = data?.configured ?? false;

  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-6">
      <div className="mb-5 flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-neutral-800 text-neutral-300">
          <Sparkles className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-neutral-100">
            OpenAI — Assistente de IA
          </h2>
          <p className="mt-0.5 text-sm text-neutral-400">
            Chave de API usada pelo assistente conversacional dos escritórios.
          </p>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-neutral-500">Carregando...</p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {isConfigured && (
            <div className="rounded-md border border-neutral-700 bg-neutral-800 px-4 py-3 text-sm text-neutral-300">
              <span className="font-medium text-neutral-100">Configurado.</span>{' '}
              Chave atual:{' '}
              <code className="rounded bg-neutral-700 px-1.5 py-0.5 font-mono text-xs text-neutral-200">
                {data?.keyMasked ?? '••••'}
              </code>
            </div>
          )}

          <div className="space-y-1.5">
            <label htmlFor="openai-api-key" className="block text-sm font-medium text-neutral-300">
              {isConfigured ? 'Nova API Key' : 'API Key'}
            </label>
            <input
              id="openai-api-key"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={isConfigured ? 'Deixe vazio para manter a atual' : 'sk-...'}
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
              required={!isConfigured}
              autoComplete="new-password"
            />
            {isConfigured && (
              <p className="text-xs text-neutral-500">
                A chave atual é mantida se este campo ficar vazio.
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              onClick={() => setEnabled((v) => !v)}
              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2 focus:ring-offset-neutral-900 ${
                enabled ? 'bg-violet-600' : 'bg-neutral-700'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  enabled ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </button>
            <span className="text-sm text-neutral-300">
              Integração {enabled ? 'ativa' : 'desativada'}
            </span>
          </div>

          {mutation.isError && (
            <p className="text-sm text-red-400">{getErrorMessage(mutation.error)}</p>
          )}

          {mutation.isSuccess && (
            <p className="text-sm text-green-400">Configuração salva com sucesso.</p>
          )}

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={mutation.isPending}
              className="rounded-md bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-900 hover:bg-white disabled:opacity-50"
            >
              {mutation.isPending ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modelos de IA por finalidade
//
// Config de plataforma (nao por tenant) que decide qual modelo cada tipo de
// trabalho usa. Serve para medir custo x qualidade sem deploy: o campo aceita
// qualquer id de modelo, inclusive um que ainda nao esta na lista.
// ---------------------------------------------------------------------------

const CUSTOM_OPTION = '__custom__';

/** Providers disponiveis. Ao plugar um novo no backend, adicione o id aqui. */
const AI_PROVIDERS: { id: AiProviderId; label: string }[] = [{ id: 'openai', label: 'OpenAI' }];

interface PurposeMeta {
  purpose: AiPurpose;
  title: string;
  /** O que essa finalidade faz e qual o trade-off da escolha. */
  hint: string;
  /** Sugestoes; o campo continua livre. */
  options: { id: string; note: string }[];
}

const PURPOSE_META: PurposeMeta[] = [
  {
    purpose: 'conversation',
    title: 'Conversa (texto)',
    hint: 'Modelo do chat do assistente. É o que roda em quase toda mensagem, então é onde o custo se acumula. Evite modelos de raciocínio (série "o"): o assistente usa temperatura 0, que eles não aceitam.',
    options: [
      { id: 'gpt-4o-mini', note: 'padrão · mais barato, suficiente para consultas' },
      { id: 'gpt-4o', note: 'melhor raciocínio, custo bem maior por mensagem' },
      { id: 'gpt-4.1-mini', note: 'alternativa barata, contexto maior' },
      { id: 'gpt-4.1', note: 'alternativa mais forte, custo maior' },
    ],
  },
  {
    purpose: 'vision',
    title: 'Imagem e documento (visão)',
    hint: 'Usado para ler imagem ou PDF anexado ao chat. Aqui a precisão vale mais que o preço: o volume é baixo (uma leitura por anexo) e um erro de OCR vira dado errado no sistema.',
    options: [
      { id: 'gpt-4o', note: 'padrão · leitura confiável de documento escaneado' },
      { id: 'gpt-4o-mini', note: 'bem mais barato, erra mais em letra pequena' },
      { id: 'gpt-4.1', note: 'alternativa mais forte' },
      { id: 'gpt-4.1-mini', note: 'alternativa intermediária' },
    ],
  },
  {
    purpose: 'transcription',
    title: 'Áudio (transcrição)',
    hint: 'Converte a mensagem de voz em texto. Ainda não está em uso: fica configurável desde já para a etapa de voz do chat.',
    options: [
      { id: 'whisper-1', note: 'padrão · mais barato por minuto, com marcação de tempo' },
      { id: 'gpt-4o-mini-transcribe', note: 'mais preciso, custo maior' },
      { id: 'gpt-4o-transcribe', note: 'mais preciso ainda, mais caro' },
    ],
  },
];

const SOURCE_BADGE: Record<'config' | 'env' | 'default', string | null> = {
  config: null,
  env: 'definido por variável de ambiente',
  default: 'usando o padrão',
};

const AI_INPUT_CLASS =
  'w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500';

function AiModelsCard() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'ai-models'],
    queryFn: () => apiClient.adminGetAiModels(),
  });

  const [values, setValues] = useState<Record<AiPurpose, AiModelSelection> | null>(null);
  // Finalidades em modo "outro" (id digitado a mão em vez de escolhido na lista).
  const [customMode, setCustomMode] = useState<Partial<Record<AiPurpose, boolean>>>({});

  useEffect(() => {
    if (!data) return;
    const next = {} as Record<AiPurpose, AiModelSelection>;
    const custom: Partial<Record<AiPurpose, boolean>> = {};
    for (const meta of PURPOSE_META) {
      const current = data.purposes[meta.purpose];
      next[meta.purpose] = { provider: current.provider, model: current.model };
      // Modelo gravado que nao esta na lista abre direto no campo livre.
      custom[meta.purpose] = !meta.options.some((o) => o.id === current.model);
    }
    setValues(next);
    setCustomMode(custom);
  }, [data]);

  const mutation = useMutation({
    mutationFn: (input: UpdateAiModelsIntegrationInput) => apiClient.adminUpdateAiModels(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'ai-models'] });
    },
  });

  function setSelection(purpose: AiPurpose, patch: Partial<AiModelSelection>) {
    setValues((prev) => (prev ? { ...prev, [purpose]: { ...prev[purpose], ...patch } } : prev));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!values) return;
    mutation.mutate(values);
  }

  const canSave =
    !!values && PURPOSE_META.every((m) => values[m.purpose].model.trim().length > 0);

  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-6">
      <div className="mb-5 flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-neutral-800 text-neutral-300">
          <Cpu className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-neutral-100">Modelos de IA por finalidade</h2>
          <p className="mt-0.5 text-sm text-neutral-400">
            Escolha qual modelo atende cada tipo de trabalho do assistente. Modelo mais forte
            custa mais por chamada; modelo mais barato erra mais. A chave de API continua sendo
            a configurada acima.
          </p>
        </div>
      </div>

      {isLoading || !values ? (
        <p className="text-sm text-neutral-500">Carregando...</p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {PURPOSE_META.map((meta) => {
            const value = values[meta.purpose];
            const resolved = data?.purposes[meta.purpose];
            const fallbackBadge = resolved ? SOURCE_BADGE[resolved.source] : null;
            const isCustom = customMode[meta.purpose] ?? false;
            const defaultModel = data?.defaults[meta.purpose];
            const isDefault =
              !!defaultModel &&
              defaultModel.model === value.model &&
              defaultModel.provider === value.provider;

            return (
              <div
                key={meta.purpose}
                className="space-y-2 rounded-md border border-neutral-800 bg-neutral-900/60 p-4"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-neutral-200">{meta.title}</span>
                  {fallbackBadge && (
                    <span className="rounded bg-neutral-800 px-1.5 py-0.5 text-[11px] text-neutral-400">
                      {fallbackBadge}
                    </span>
                  )}
                </div>
                <p className="text-xs leading-relaxed text-neutral-500">{meta.hint}</p>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[10rem_1fr]">
                  <div className="space-y-1.5">
                    <label
                      htmlFor={`ai-provider-${meta.purpose}`}
                      className="block text-xs font-medium text-neutral-400"
                    >
                      Provedor
                    </label>
                    {AI_PROVIDERS.length > 1 ? (
                      <select
                        id={`ai-provider-${meta.purpose}`}
                        value={value.provider}
                        onChange={(e) =>
                          setSelection(meta.purpose, {
                            provider: e.target.value as AiProviderId,
                          })
                        }
                        className={AI_INPUT_CLASS}
                      >
                        {AI_PROVIDERS.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p className="px-1 py-2 text-sm text-neutral-300">
                        {AI_PROVIDERS[0].label}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <label
                      htmlFor={`ai-model-${meta.purpose}`}
                      className="block text-xs font-medium text-neutral-400"
                    >
                      Modelo
                    </label>
                    <select
                      id={`ai-model-${meta.purpose}`}
                      value={isCustom ? CUSTOM_OPTION : value.model}
                      onChange={(e) => {
                        const picked = e.target.value;
                        if (picked === CUSTOM_OPTION) {
                          setCustomMode((prev) => ({ ...prev, [meta.purpose]: true }));
                          return;
                        }
                        setCustomMode((prev) => ({ ...prev, [meta.purpose]: false }));
                        setSelection(meta.purpose, { model: picked });
                      }}
                      className={AI_INPUT_CLASS}
                    >
                      {meta.options.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.id} · {o.note}
                        </option>
                      ))}
                      <option value={CUSTOM_OPTION}>Outro (digitar o id)</option>
                    </select>

                    {isCustom && (
                      <input
                        type="text"
                        value={value.model}
                        onChange={(e) => setSelection(meta.purpose, { model: e.target.value })}
                        placeholder="ex: gpt-4.1-nano"
                        aria-label={`Id do modelo para ${meta.title}`}
                        className={`${AI_INPUT_CLASS} font-mono`}
                      />
                    )}

                    <div className="flex items-center gap-3 text-xs">
                      <code className="rounded bg-neutral-800 px-1.5 py-0.5 font-mono text-[11px] text-neutral-300">
                        {value.model || '(vazio)'}
                      </code>
                      {!isDefault && defaultModel && (
                        <button
                          type="button"
                          onClick={() => {
                            setCustomMode((prev) => ({
                              ...prev,
                              [meta.purpose]: !meta.options.some(
                                (o) => o.id === defaultModel.model,
                              ),
                            }));
                            setSelection(meta.purpose, defaultModel);
                          }}
                          className="text-neutral-400 underline underline-offset-2 hover:text-neutral-200"
                        >
                          Restaurar padrão ({defaultModel.model})
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {mutation.isError && (
            <p className="text-sm text-red-400">{getErrorMessage(mutation.error)}</p>
          )}
          {mutation.isSuccess && (
            <p className="text-sm text-green-400">Modelos salvos com sucesso.</p>
          )}

          <div className="flex justify-end pt-1">
            <button
              type="submit"
              disabled={mutation.isPending || !canSave}
              className="rounded-md bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-900 hover:bg-white disabled:opacity-50"
            >
              {mutation.isPending ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Email SMTP global — servidor usado para todos os e-mails transacionais do
// sistema (o mais importante hoje: primeiro acesso pos-assinatura). Resend
// continua como fallback quando o SMTP global nao esta configurado/falha.
// ---------------------------------------------------------------------------

const ALLOWED_SMTP_PORTS = [25, 465, 587, 2525] as const;

const emailSmtpFormSchema = z.object({
  host: z.string().min(1, 'Host obrigatório').max(255),
  port: z
    .number({ invalid_type_error: 'Porta inválida' })
    .int()
    .refine((p) => (ALLOWED_SMTP_PORTS as readonly number[]).includes(p), {
      message: `Porta deve ser uma de: ${ALLOWED_SMTP_PORTS.join(', ')}`,
    }),
  secure: z.boolean(),
  username: z.string().min(1, 'Usuário obrigatório').max(300),
  password: z.string().max(500).optional(),
  from: z.string().min(3, 'Remetente obrigatório').max(200),
  enabled: z.boolean(),
});

type EmailSmtpFormValues = z.infer<typeof emailSmtpFormSchema>;

/** Toggle reutilizado pelos dois switches deste card (SSL e Ativo). */
function ToggleField({
  control,
  name,
  label,
  hint,
}: {
  control: Control<EmailSmtpFormValues>;
  name: 'secure' | 'enabled';
  label: (checked: boolean) => string;
  hint?: string;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <div className="flex items-start gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={field.value}
            onClick={() => field.onChange(!field.value)}
            className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2 focus:ring-offset-neutral-900 ${
              field.value ? 'bg-violet-600' : 'bg-neutral-700'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                field.value ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </button>
          <div>
            <span className="text-sm text-neutral-300">{label(field.value)}</span>
            {hint && <p className="text-xs text-neutral-500">{hint}</p>}
          </div>
        </div>
      )}
    />
  );
}

function EmailSmtpCard() {
  const queryClient = useQueryClient();
  const currentUserEmail = useAuthStore((s) => s.user?.email);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'email-smtp-integration'],
    queryFn: () => apiClient.adminGetEmailIntegration(),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<EmailSmtpFormValues>({
    resolver: zodResolver(emailSmtpFormSchema),
    defaultValues: {
      host: '',
      port: 587,
      secure: false,
      username: '',
      password: '',
      from: '',
      enabled: true,
    },
  });

  // Popula o form quando a config atual carrega. A senha nunca volta da API
  // (write-only) — o campo fica sempre vazio, com placeholder explicando.
  useEffect(() => {
    if (data) {
      reset({
        host: data.host ?? '',
        port: data.port ?? 587,
        secure: data.secure,
        username: data.username ?? '',
        password: '',
        from: data.from ?? '',
        enabled: data.enabled,
      });
    }
  }, [data, reset]);

  const mutation = useMutation({
    mutationFn: (input: UpdateEmailSmtpIntegrationInput) =>
      apiClient.adminUpdateEmailIntegration(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'email-smtp-integration'] });
      reset((current) => ({ ...current, password: '' }));
    },
  });

  function onSubmit(values: EmailSmtpFormValues) {
    const input: UpdateEmailSmtpIntegrationInput = {
      host: values.host,
      port: values.port,
      secure: values.secure,
      username: values.username,
      from: values.from,
      enabled: values.enabled,
      // So envia senha se o usuario digitou algo — vazio mantem a atual
      ...(values.password?.trim() ? { password: values.password } : {}),
    };
    mutation.mutate(input);
  }

  // Envio de e-mail de teste — acao independente do Salvar, usa a config ja
  // persistida no backend. Campo "to" pre-populado com o e-mail do super
  // admin logado, editavel.
  const [testTo, setTestTo] = useState('');
  useEffect(() => {
    if (currentUserEmail) {
      setTestTo((prev) => prev || currentUserEmail);
    }
  }, [currentUserEmail]);

  const testMutation = useMutation({
    mutationFn: (to: string) => apiClient.adminTestEmailIntegration(to),
  });

  const isConfigured = data?.configured ?? false;

  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-6">
      <div className="mb-5 flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-neutral-800 text-neutral-300">
          <Mail className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-neutral-100">
            Envio de e-mail (SMTP)
          </h2>
          <p className="mt-0.5 text-sm text-neutral-400">
            Servidor SMTP global usado nos e-mails transacionais do sistema
            (ex: primeiro acesso pós-assinatura). Resend é usado como
            fallback quando indisponível.
          </p>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-neutral-500">Carregando...</p>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {isConfigured && (
            <div className="rounded-md border border-neutral-700 bg-neutral-800 px-4 py-3 text-sm text-neutral-300">
              <span className="font-medium text-neutral-100">Configurado.</span>{' '}
              Host atual:{' '}
              <code className="rounded bg-neutral-700 px-1.5 py-0.5 font-mono text-xs text-neutral-200">
                {data?.host}:{data?.port}
              </code>
              <span className="ml-3 text-neutral-400">· Senha configurada</span>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5 sm:col-span-2">
              <label htmlFor="smtp-host" className="block text-sm font-medium text-neutral-300">
                Host
              </label>
              <input
                id="smtp-host"
                type="text"
                {...register('host')}
                placeholder="smtp-relay.brevo.com"
                className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
              />
              {errors.host && <p className="text-xs text-red-400">{errors.host.message}</p>}
            </div>

            <div className="space-y-1.5">
              <label htmlFor="smtp-port" className="block text-sm font-medium text-neutral-300">
                Porta
              </label>
              <input
                id="smtp-port"
                type="text"
                inputMode="numeric"
                {...register('port', { valueAsNumber: true })}
                placeholder="587"
                className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
              />
              {errors.port && <p className="text-xs text-red-400">{errors.port.message}</p>}
            </div>
          </div>

          <ToggleField
            control={control}
            name="secure"
            label={(checked) => `Conexão segura (SSL) ${checked ? 'ligada' : 'desligada'}`}
            hint="Desligado para porta 587 (STARTTLS) · Ligado para porta 465 (SSL/TLS implícito)"
          />

          <div className="space-y-1.5">
            <label htmlFor="smtp-username" className="block text-sm font-medium text-neutral-300">
              Usuário
            </label>
            <input
              id="smtp-username"
              type="text"
              {...register('username')}
              placeholder="usuario@provedor-smtp.com"
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
            />
            {errors.username && <p className="text-xs text-red-400">{errors.username.message}</p>}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="smtp-password" className="block text-sm font-medium text-neutral-300">
              {isConfigured ? 'Nova senha' : 'Senha'}
            </label>
            <input
              id="smtp-password"
              type="password"
              {...register('password')}
              placeholder="deixe em branco para manter"
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
              autoComplete="new-password"
            />
            {errors.password && <p className="text-xs text-red-400">{errors.password.message}</p>}
            {isConfigured && (
              <p className="text-xs text-neutral-500">
                A senha atual é mantida se este campo ficar vazio.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="smtp-from" className="block text-sm font-medium text-neutral-300">
              Remetente (From)
            </label>
            <input
              id="smtp-from"
              type="text"
              {...register('from')}
              placeholder="VoxisAdv <no-reply@voxisadv.com.br>"
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
            />
            {errors.from && <p className="text-xs text-red-400">{errors.from.message}</p>}
          </div>

          <ToggleField
            control={control}
            name="enabled"
            label={(checked) => `Integração ${checked ? 'ativa' : 'desativada'}`}
          />

          {mutation.isError && (
            <p className="text-sm text-red-400">{getErrorMessage(mutation.error)}</p>
          )}
          {mutation.isSuccess && (
            <p className="text-sm text-green-400">Configuração salva com sucesso.</p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-800 pt-4">
            <div className="flex items-center gap-2">
              <input
                type="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="destino@exemplo.com"
                aria-label="E-mail de destino do teste"
                className="w-56 rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
              />
              <button
                type="button"
                disabled={testMutation.isPending || !testTo.trim()}
                onClick={() => testMutation.mutate(testTo.trim())}
                className="rounded-md border border-neutral-700 bg-neutral-800 px-4 py-2 text-sm font-medium text-neutral-200 hover:bg-neutral-700 disabled:opacity-50"
              >
                {testMutation.isPending ? 'Enviando...' : 'Enviar e-mail de teste'}
              </button>
            </div>

            <button
              type="submit"
              disabled={mutation.isPending}
              className="rounded-md bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-900 hover:bg-white disabled:opacity-50"
            >
              {mutation.isPending ? 'Salvando...' : 'Salvar'}
            </button>
          </div>

          {testMutation.data && (
            <p
              className={`text-sm ${testMutation.data.ok ? 'text-green-400' : 'text-red-400'}`}
            >
              {testMutation.data.message}
            </p>
          )}
          {testMutation.isError && (
            <p className="text-sm text-red-400">{getErrorMessage(testMutation.error)}</p>
          )}
        </form>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Armazenamento S3 — bucket S3-compativel (MinIO) usado para gravar novos
// arquivos quando ativo. Enquanto desativado, o storage local do disco
// continua sendo usado (StorageProviderResolver, apps/api).
// ---------------------------------------------------------------------------

const storageS3FormSchema = z.object({
  endpoint: z
    .string()
    .trim()
    .min(1, 'Endpoint obrigatório')
    .max(500)
    .refine((v) => /^https?:\/\//i.test(v), {
      message: 'Endpoint deve começar com http:// ou https://',
    }),
  region: z.string().trim().min(1, 'Região obrigatória').max(100),
  bucket: z
    .string()
    .trim()
    .min(3, 'Bucket deve ter ao menos 3 caracteres')
    .max(63, 'Bucket deve ter no máximo 63 caracteres')
    .regex(
      /^[a-z0-9][a-z0-9.-]*[a-z0-9]$/,
      'Bucket deve conter apenas minúsculas, dígitos, ponto e hífen',
    ),
  accessKey: z.string().trim().min(1, 'Access key obrigatória').max(500),
  secretKey: z.string().max(1000).optional(),
  enabled: z.boolean(),
});

type StorageS3FormValues = z.infer<typeof storageS3FormSchema>;

/** Toggle reutilizado so por este card — ToggleField acima e tipado para EmailSmtpFormValues. */
function StorageToggleField({
  control,
  label,
  hint,
}: {
  control: Control<StorageS3FormValues>;
  label: (checked: boolean) => string;
  hint?: string;
}) {
  return (
    <Controller
      control={control}
      name="enabled"
      render={({ field }) => (
        <div className="flex items-start gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={field.value}
            onClick={() => field.onChange(!field.value)}
            className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2 focus:ring-offset-neutral-900 ${
              field.value ? 'bg-violet-600' : 'bg-neutral-700'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                field.value ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </button>
          <div>
            <span className="text-sm text-neutral-300">{label(field.value)}</span>
            {hint && <p className="text-xs text-neutral-500">{hint}</p>}
          </div>
        </div>
      )}
    />
  );
}

function StorageS3Card() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'storage-s3-integration'],
    queryFn: () => apiClient.adminGetStorageS3Integration(),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<StorageS3FormValues>({
    resolver: zodResolver(storageS3FormSchema),
    defaultValues: {
      endpoint: '',
      region: '',
      bucket: '',
      accessKey: '',
      secretKey: '',
      enabled: false,
    },
  });

  // Popula o form quando a config atual carrega. endpoint/region/bucket
  // voltam completos da API; accessKey e secretKey nao (mascarado/write-only
  // — mesmo padrao do clientId/clientSecret do Pluggy), entao ficam vazios
  // e precisam ser redigitados a cada salvamento.
  useEffect(() => {
    if (data) {
      reset({
        endpoint: data.endpoint ?? '',
        region: data.region ?? '',
        bucket: data.bucket ?? '',
        accessKey: '',
        secretKey: '',
        enabled: data.enabled,
      });
    }
  }, [data, reset]);

  const mutation = useMutation({
    mutationFn: (input: UpdateStorageS3IntegrationInput) =>
      apiClient.adminUpdateStorageS3Integration(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'storage-s3-integration'] });
      reset((current) => ({ ...current, secretKey: '' }));
    },
  });

  function onSubmit(values: StorageS3FormValues) {
    const input: UpdateStorageS3IntegrationInput = {
      endpoint: values.endpoint,
      region: values.region,
      bucket: values.bucket,
      accessKey: values.accessKey,
      enabled: values.enabled,
      // So envia secret se o usuario digitou algo — vazio mantem a atual
      ...(values.secretKey?.trim() ? { secretKey: values.secretKey } : {}),
    };
    mutation.mutate(input);
  }

  // Testar conexao — sempre testa a config JA GRAVADA no backend (nao o
  // form em edicao), mesma semantica do teste de e-mail. Roda mesmo com
  // enabled=false.
  const testMutation = useMutation({
    mutationFn: () => apiClient.adminTestStorageS3Integration(),
  });

  const isConfigured = data?.configured ?? false;

  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-6">
      <div className="mb-5 flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-neutral-800 text-neutral-300">
          <HardDrive className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-neutral-100">
            Armazenamento S3
          </h2>
          <p className="mt-0.5 text-sm text-neutral-400">
            Bucket S3-compatível (MinIO) usado para gravar os arquivos dos escritórios. Enquanto
            desativado, os arquivos continuam sendo salvos em disco local.
          </p>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-neutral-500">Carregando...</p>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {isConfigured && (
            <div className="rounded-md border border-neutral-700 bg-neutral-800 px-4 py-3 text-sm text-neutral-300">
              <span className="font-medium text-neutral-100">Configurado.</span>{' '}
              Bucket atual:{' '}
              <code className="rounded bg-neutral-700 px-1.5 py-0.5 font-mono text-xs text-neutral-200">
                {data?.bucket}
              </code>
              <span className="ml-3 text-neutral-400">
                · Access key {data?.accessKeyMasked} · Secret{' '}
                {data?.secretKeyConfigured ? 'configurada' : 'não configurada'}
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5 sm:col-span-2">
              <label htmlFor="s3-endpoint" className="block text-sm font-medium text-neutral-300">
                Endpoint
              </label>
              <input
                id="s3-endpoint"
                type="text"
                {...register('endpoint')}
                placeholder="https://s3.minio.exemplo.com.br"
                className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
              />
              {errors.endpoint && (
                <p className="text-xs text-red-400">{errors.endpoint.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <label htmlFor="s3-region" className="block text-sm font-medium text-neutral-300">
                Região
              </label>
              <input
                id="s3-region"
                type="text"
                {...register('region')}
                placeholder="us-east-1"
                className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
              />
              {errors.region && <p className="text-xs text-red-400">{errors.region.message}</p>}
            </div>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="s3-bucket" className="block text-sm font-medium text-neutral-300">
              Bucket
            </label>
            <input
              id="s3-bucket"
              type="text"
              {...register('bucket')}
              placeholder="proadv-storage"
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
            />
            {errors.bucket && <p className="text-xs text-red-400">{errors.bucket.message}</p>}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="s3-access-key" className="block text-sm font-medium text-neutral-300">
              {isConfigured ? 'Nova Access Key' : 'Access Key'}
            </label>
            <input
              id="s3-access-key"
              type="text"
              {...register('accessKey')}
              placeholder={isConfigured ? `Atual: ${data?.accessKeyMasked ?? '••••'}` : 'AKIA...'}
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
            />
            {errors.accessKey && (
              <p className="text-xs text-red-400">{errors.accessKey.message}</p>
            )}
            {isConfigured && (
              <p className="text-xs text-neutral-500">
                Precisa ser redigitada a cada salvamento (a API não devolve o valor completo).
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="s3-secret-key" className="block text-sm font-medium text-neutral-300">
              {isConfigured ? 'Nova Secret Key' : 'Secret Key'}
            </label>
            <input
              id="s3-secret-key"
              type="password"
              {...register('secretKey')}
              placeholder={isConfigured ? '••••••••••••••• (deixe em branco para manter)' : 'Cole a secret key aqui'}
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
              autoComplete="new-password"
            />
            {errors.secretKey && (
              <p className="text-xs text-red-400">{errors.secretKey.message}</p>
            )}
            {isConfigured && (
              <p className="text-xs text-neutral-500">
                A secret atual é mantida se este campo ficar vazio.
              </p>
            )}
          </div>

          <StorageToggleField
            control={control}
            label={(checked) => `Armazenamento S3 ${checked ? 'ativo' : 'desativado'}`}
            hint="Ao ativar, os arquivos novos passam a ser gravados no S3. Arquivos já salvos em disco local não são migrados automaticamente."
          />

          {mutation.isError && (
            <p className="text-sm text-red-400">{getErrorMessage(mutation.error)}</p>
          )}
          {mutation.isSuccess && (
            <p className="text-sm text-green-400">Configuração salva com sucesso.</p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-800 pt-4">
            <button
              type="button"
              disabled={testMutation.isPending}
              onClick={() => testMutation.mutate()}
              className="rounded-md border border-neutral-700 bg-neutral-800 px-4 py-2 text-sm font-medium text-neutral-200 hover:bg-neutral-700 disabled:opacity-50"
            >
              {testMutation.isPending ? 'Testando...' : 'Testar conexão'}
            </button>

            <button
              type="submit"
              disabled={mutation.isPending}
              className="rounded-md bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-900 hover:bg-white disabled:opacity-50"
            >
              {mutation.isPending ? 'Salvando...' : 'Salvar'}
            </button>
          </div>

          {testMutation.data && (
            <p className={`text-sm ${testMutation.data.ok ? 'text-green-400' : 'text-red-400'}`}>
              {testMutation.data.message}
            </p>
          )}
          {testMutation.isError && (
            <p className="text-sm text-red-400">{getErrorMessage(testMutation.error)}</p>
          )}
        </form>
      )}
    </div>
  );
}
